import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { money } from '@/components/ewayBill/shared';

export const BLANK_ITEM = { name: '', description: '', hsn: '', qty: '', unit: 'PCS', rate: '', taxable_value: '', gst_rate: 5 };
const COLS = [
  ['name', 'Item code / Tally item', 'w-32 font-mono'], ['description', 'Description', 'w-44'], ['hsn', 'HSN', 'w-24 font-mono'],
  ['qty', 'Qty', 'w-16 text-right'], ['unit', 'Unit', 'w-14'], ['rate', 'Rate', 'w-20 text-right'],
  ['taxable_value', 'Amount', 'w-24 text-right'], ['gst_rate', 'GST %', 'w-14 text-right'],
];
const NUM = new Set(['qty', 'rate', 'taxable_value', 'gst_rate']);
const n = (v) => Number(String(v ?? '').replace(/,/g, '')) || 0;
const cell = 'h-8 rounded border border-gray-300 bg-white px-1.5 text-xs focus:border-[#2D5F4F] focus:outline-none';

/** Editable item lines of a manual DC — amount follows qty × rate unless typed. */
export const ManualItemsEditor = ({ items, onChange }) => {
  const set = (idx, k, v) => onChange(items.map((it, i) => {
    if (i !== idx) return it;
    const x = { ...it, [k]: v };
    if (k === 'qty' || k === 'rate') x.taxable_value = n(x.qty) > 0 && n(x.rate) > 0 ? Math.round(n(x.qty) * n(x.rate) * 100) / 100 : '';
    return x;
  }));
  const qty = items.reduce((s, i) => s + n(i.qty), 0);
  const amt = items.reduce((s, i) => s + n(i.taxable_value), 0);
  return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid="manual-items">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[11px] uppercase text-gray-500"><tr>{COLS.map(([k, l]) => <th key={k} className="px-1.5 py-1.5 text-left font-medium">{l}</th>)}<th /></tr></thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={idx} className="border-t" data-testid={`manual-item-${idx}`}>
                {COLS.map(([k, , w]) => (
                  <td key={k} className="px-1 py-1">
                    <input value={it[k] ?? ''} onChange={(e) => set(idx, k, e.target.value)} inputMode={NUM.has(k) ? 'decimal' : undefined}
                      className={`${cell} ${w}`} data-testid={`manual-item-${idx}-${k}`} />
                  </td>
                ))}
                <td className="px-1"><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => onChange(items.filter((_, i) => i !== idx))} disabled={items.length < 2} data-testid={`manual-item-del-${idx}`}><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 border-t px-3 py-2 text-sm">
        <Button size="sm" variant="outline" onClick={() => onChange([...items, { ...BLANK_ITEM }])} data-testid="manual-item-add"><Plus className="w-4 h-4 mr-1" />Add line</Button>
        <span className="ml-auto">{items.length} line(s) · <b className="tabular-nums" data-testid="manual-total-qty">{qty}</b> qty · <b className="tabular-nums" data-testid="manual-total-amount">{money(amt)}</b></span>
      </div>
    </div>
  );
};

export default ManualItemsEditor;
